import { useState, useCallback, createContext, useContext } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

// Create context for the confirm dialog
const ConfirmContext = createContext(null);

/**
 * ConfirmDialogProvider - Wrap your app with this to enable useConfirm hook
 */
export const ConfirmDialogProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    resolvePromise: null,
  });

  const confirm = useCallback(({ title, message, type = 'warning', confirmText = 'Confirm', cancelText = 'Cancel' }) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText,
        resolvePromise: resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setDialogState((prev) => {
      prev.resolvePromise?.(true);
      return { ...prev, isOpen: false, resolvePromise: null };
    });
  }, []);

  const handleCancel = useCallback(() => {
    setDialogState((prev) => {
      prev.resolvePromise?.(false);
      return { ...prev, isOpen: false, resolvePromise: null };
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * useConfirm - Hook to show a confirmation dialog
 * 
 * Usage:
 * const { confirm } = useConfirm();
 * const confirmed = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?',
 *   type: 'danger',
 *   confirmText: 'Delete',
 *   cancelText: 'Cancel'
 * });
 * if (confirmed) { ... }
 */
export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  
  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  
  return { confirm };
};

export default useConfirm;